// src/components/foam/FoamOwnerDetail.js
//
// Screen 5 · Owner Board Detail (`/boards/:sku`)
// Doc: handoff README "Screen 5" + SPEC §2.2 (amended 2026-06-05)
//
// 2026-06-05 (Nigel):
//   - Inline EDIT capability for First name, City, Owner message, Phone —
//     persisted to customDisplayName / ownerCity / ownerMessage / phoneNumber.
//   - "Via WAKA" contact model REMOVED. Preferred contact = Phone/Email only.
//   - Owner message gets a boilerplate default (SPEC §2.4 suggested copy).
//   - NOTE: ToggleRow/EditInput live at MODULE scope. Defining them inside
//     the component remounts the input on every keystroke (focus loss /
//     text garbling — bug Nigel hit on prod). Keep them out here.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FPage, FHeader, FLabel, FHeadline } from './FoamPrimitives';
import FoamToggle from './FoamToggle';

const OWNER_MESSAGE_MAX = 280; // SPEC §2.4
const DEFAULT_OWNER_MESSAGE =
  "If you've found my board, you've made my day. Get in touch and let's reunite us.";

// ── module-scope helpers (stable component identity across renders) ─────────

const EditInput = ({ field, value, onChange, placeholder, multiline = false, maxLength }) => {
  const common = {
    value,
    onChange: (e) => onChange(e.target.value),
    placeholder,
    maxLength,
    className: `
      w-full font-sans text-[15px] mt-[10px] px-[14px]
      bg-foam-paper-2 text-foam-ink placeholder:text-foam-mute
      focus:outline-none focus:ring-2 focus:ring-foam-ink border-0
    `,
  };
  return multiline ? (
    <div>
      <textarea
        {...common}
        rows={3}
        style={{ borderRadius: 0, paddingTop: 12, paddingBottom: 12, resize: 'none' }}
      />
      <div className="font-mono text-[12px] text-foam-mute text-right mt-[2px]">
        {value.length}/{maxLength}
      </div>
    </div>
  ) : (
    <input
      {...common}
      type={field === 'phone' ? 'tel' : 'text'}
      style={{ borderRadius: 0, height: 48 }}
    />
  );
};

const ToggleRow = ({
  title, sub, value, onToggle,
  editField, editValue, onEditChange, editPlaceholder, multiline, maxLength,
  editing, setEditing,
}) => (
  <div className="py-[14px]" style={{ borderBottom: '1px solid var(--foam-hair)' }}>
    <div className="flex items-center justify-between gap-[14px]">
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-medium flex items-center">
          {title}
          {editField && (
            <button
              type="button"
              onClick={() => setEditing(editing === editField ? null : editField)}
              className="font-mono text-[12px] tracking-[0.08em] uppercase underline text-foam-ink-2 ml-[8px]"
              style={{ background: 'transparent' }}
            >
              {editing === editField ? 'Done' : 'Edit'}
            </button>
          )}
        </div>
        {sub && (
          <div className="text-[12px] text-foam-ink-2 mt-[2px] truncate">{sub}</div>
        )}
      </div>
      <FoamToggle on={value} onChange={onToggle} ariaLabel={title} />
    </div>
    {editField && editing === editField && (
      <EditInput
        field={editField}
        value={editValue}
        onChange={onEditChange}
        placeholder={editPlaceholder}
        multiline={multiline}
        maxLength={maxLength}
      />
    )}
  </div>
);

// ── screen ───────────────────────────────────────────────────────────────────

const FoamOwnerDetail = ({
  product,           // { name, image, serial, version, productName }
  registration,     // existing registration document
  onSave,
  onPreviewAsFinder,
  onWarranty,        // → /boards/:sku/warranty (policy from waka.surf)
  onSupport,         // → /boards/:sku/support (Viento chat)
  onDone,
}) => {
  // Initialise local toggle state from the registration's findability object,
  // or derive sensible defaults if the row is legacy (pre-2026-05-20 schema).
  const f = registration?.findability || {};
  const isLegacy = !registration?.findability;
  const legacyShowName = registration?.showName ?? true;
  const legacyShowEmail = registration?.showEmail ?? false;

  const [enabled, setEnabled] = useState(f.enabled ?? true);
  const [showFirstName, setShowFirstName] = useState(f.showFirstName ?? legacyShowName);
  const [showCity, setShowCity] = useState(f.showCity ?? false);
  const [showOwnerMessage, setShowOwnerMessage] = useState(f.showOwnerMessage ?? !!registration?.contactMessage);
  const [showEmail, setShowEmail] = useState(f.showEmail ?? legacyShowEmail);
  const [showPhone, setShowPhone] = useState(f.showPhone ?? false);
  // WhatsApp rides on the same number — finders get a wa.me button too
  const [phoneIsWhatsApp, setPhoneIsWhatsApp] = useState(f.phoneIsWhatsApp ?? false);
  // 'phone' | 'email' | null — legacy 'via-waka' values map to null (removed 2026-06-05)
  const initialPreferred = f.preferredContact === 'phone' || f.preferredContact === 'email' ? f.preferredContact : null;
  const [preferredContact, setPreferredContact] = useState(initialPreferred);

  // Editable values (Edit affordance — Nigel 2026-06-05). Owner message
  // defaults to warm boilerplate so the field is never a blank page.
  const [displayName, setDisplayName] = useState(registration?.customDisplayName || registration?.registeredUserName || '');
  const [city, setCity] = useState(registration?.ownerCity || '');
  const [ownerMessage, setOwnerMessage] = useState(
    registration?.ownerMessage || registration?.contactMessage || DEFAULT_OWNER_MESSAGE
  );
  const [phone, setPhone] = useState(registration?.phoneNumber || '');
  const [editing, setEditing] = useState(null); // which field is being edited

  const email = registration?.registeredUserEmail || '';
  const firstName = displayName.trim().split(' ')[0] || '';

  // Keep preferredContact coherent with the channel toggles.
  const togglePhone = (on) => {
    setShowPhone(on);
    if (on && !preferredContact) setPreferredContact('phone');
    if (!on && preferredContact === 'phone') setPreferredContact(showEmail ? 'email' : null);
  };
  const toggleEmail = (on) => {
    setShowEmail(on);
    if (on && !preferredContact) setPreferredContact('email');
    if (!on && preferredContact === 'email') setPreferredContact(showPhone ? 'phone' : null);
  };

  const rowEditProps = { editing, setEditing };

  // ── Auto-save (Nigel, 2026-06-05 — replaces the Save button) ─────────────
  // Debounced ~1s after the last change; "Done" flushes anything pending.
  const [saveState, setSaveState] = useState('idle'); // idle|pending|saving|saved|error
  const saveTimer = useRef(null);
  const mounted = useRef(false);

  const buildUpdates = useCallback(() => ({
    customDisplayName: displayName.trim(),
    ownerCity: city.trim(),
    ownerMessage: ownerMessage.trim(),
    phoneNumber: phone.trim(),
    findability: {
      enabled,
      showFirstName,
      showCity,
      showOwnerMessage,
      showEmail,
      showPhone,
      phoneIsWhatsApp, // same number, wa.me deep link for finders
      preferredContact, // 'phone' | 'email' | null
    },
  }), [displayName, city, ownerMessage, phone, enabled, showFirstName, showCity, showOwnerMessage, showEmail, showPhone, phoneIsWhatsApp, preferredContact]);

  const flush = useCallback(async () => {
    clearTimeout(saveTimer.current);
    setSaveState('saving');
    try {
      await onSave?.(buildUpdates());
      setSaveState('saved');
    } catch (e) {
      console.error('Auto-save failed:', e);
      setSaveState('error');
    }
  }, [onSave, buildUpdates]);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setSaveState('pending');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 1000);
    return () => clearTimeout(saveTimer.current);
  }, [flush]); // flush's identity changes with every edit (buildUpdates deps)

  const handleDone = async () => {
    if (saveState === 'pending' || saveState === 'saving') await flush();
    onDone?.();
  };

  return (
    <FPage>
      <FHeader right={
        <div className="flex items-center gap-[10px]">
          <span className="font-mono text-[12px] tracking-[0.08em] uppercase text-foam-ink-2">
            {saveState === 'pending' || saveState === 'saving' ? 'Saving…'
              : saveState === 'saved' ? '✓ Saved'
              : saveState === 'error' ? '' : ''}
          </span>
          <button
            type="button"
            onClick={handleDone}
            className="font-sans text-[14px] text-foam-ink"
          >
            Done
          </button>
        </div>
      } />

      <FLabel>Your board</FLabel>

      {/* Design system: Saira Condensed 800, 64px, uppercase */}
      <FHeadline size={64} lineHeight={0.9}>
        My
        <FHeadline.Accent block>{(product?.productName || 'Board')}.</FHeadline.Accent>
      </FHeadline>

      <div className="flex justify-between items-baseline">
        <div className="font-mono text-[12px] tracking-[0.1em] uppercase text-foam-ink-2">
          {(product?.productName || 'Board').toUpperCase()} {product?.size || ''} · {product?.serial || product?.sku || ''} · {product?.version || ''}
        </div>
        {registration?.verified && (
          <div className="font-mono text-[12px] tracking-[0.1em] uppercase flex items-center gap-[6px]" style={{ color: 'var(--foam-ok)' }}>
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--foam-ok)' }}
            />
            Verified
          </div>
        )}
      </div>

      {product?.image && (
        <div className="flex items-center justify-center py-[8px]">
          <img
            src={product.image}
            alt=""
            style={{ height: 170, width: 'auto', objectFit: 'contain' }}
          />
        </div>
      )}

      {/* Findability section */}
      <div
        className="flex flex-col"
        style={{ paddingTop: 14, borderTop: '1.5px solid var(--foam-line)' }}
      >
        <div className="flex items-center justify-between mb-[8px]">
          <FLabel>Findability</FLabel>
          <span
            className="font-mono text-[12px] uppercase font-medium px-[10px] py-[4px]"
            style={{
              background: enabled ? 'var(--foam-orange)' : 'var(--foam-paper-2)',
              color: enabled ? 'var(--foam-ink)' : 'var(--foam-ink-2)',
              letterSpacing: '0.1em',
              borderRadius: 999,
            }}
          >
            {enabled ? 'On' : 'Off'}
          </span>
        </div>

        <ToggleRow
          title="Master"
          sub={enabled ? 'Your board has a public Lost & Found page.' : 'No public page — board is just a board.'}
          value={enabled}
          onToggle={setEnabled}
          {...rowEditProps}
        />

        {enabled && (
          <>
            <ToggleRow
              title="First name"
              sub={firstName || 'Not set — tap Edit to add'}
              value={showFirstName}
              onToggle={setShowFirstName}
              editField="name"
              editValue={displayName}
              onEditChange={setDisplayName}
              editPlaceholder="Your name"
              {...rowEditProps}
            />
            {/* City row removed 2026-06-05 (Nigel — low value, soft theft-target
                signal). Data fields (ownerCity/showCity) remain in the schema;
                restore the ToggleRow here if it ever earns its place. */}
            <ToggleRow
              title="Owner message"
              sub={ownerMessage ? `"${ownerMessage}"` : 'Not set — tap Edit to add'}
              value={showOwnerMessage}
              onToggle={setShowOwnerMessage}
              editField="message"
              editValue={ownerMessage}
              onEditChange={setOwnerMessage}
              editPlaceholder="A note finders will see — keep it warm."
              multiline
              maxLength={OWNER_MESSAGE_MAX}
              {...rowEditProps}
            />
            <ToggleRow
              title="Public email"
              sub={showEmail ? email : 'Off — hidden from finders'}
              value={showEmail}
              onToggle={toggleEmail}
              {...rowEditProps}
            />
            <ToggleRow
              title="Public phone"
              sub={showPhone ? (phone || 'Not set — tap Edit to add') : 'Off — hidden from finders'}
              value={showPhone}
              onToggle={togglePhone}
              editField="phone"
              editValue={phone}
              onEditChange={setPhone}
              editPlaceholder="+1 555 123 4567"
              {...rowEditProps}
            />
            {showPhone && (
              <ToggleRow
                title="WhatsApp"
                sub="Same number — finders also get a WhatsApp button"
                value={phoneIsWhatsApp}
                onToggle={setPhoneIsWhatsApp}
                {...rowEditProps}
              />
            )}

            {/* Preferred contact — only when at least one channel is shared.
                "Via WAKA" removed 2026-06-05: no channels = not contactable. */}
            {(showPhone || showEmail) && (
              <div className="pt-[18px]">
                <FLabel>Preferred contact method</FLabel>
                <div className="flex flex-col mt-[10px] gap-[10px]">
                  {[
                    { id: 'phone', label: 'Phone', available: showPhone },
                    { id: 'email', label: 'Email', available: showEmail },
                  ].filter((opt) => opt.available).map((opt) => {
                    const isSelected = preferredContact === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPreferredContact(opt.id)}
                        className="flex items-center gap-[12px] text-left cursor-pointer"
                      >
                        <span
                          className="inline-block"
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: '1.5px solid var(--foam-line)',
                            background: isSelected ? 'var(--foam-ink)' : 'transparent',
                            boxShadow: isSelected ? 'inset 0 0 0 3px var(--foam-paper)' : 'none',
                          }}
                        />
                        <span className="text-[15px]">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!showPhone && !showEmail && (
              <div className="text-[12px] text-foam-ink-2 leading-[1.5] pt-[14px]">
                No contact channel shared — finders will see your board's page
                but won't have a way to reach you. Turn on email or phone if
                you want your board to find its way home.
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview as finder card */}
      {enabled && (
        <div
          className="p-[18px] flex items-start gap-[14px]"
          style={{ background: 'var(--foam-carbon)', color: 'var(--foam-paper)' }}
        >
          <div
            className="flex items-center justify-center font-sans font-bold flex-shrink-0 text-[22px]"
            style={{ width: 44, height: 44, background: 'var(--foam-orange)', color: 'var(--foam-carbon)' }}
          >
            ?
          </div>
          <div className="flex-1">
            <div className="text-[15px] font-bold" style={{ letterSpacing: '-0.01em' }}>Preview as a finder</div>
            <div className="text-[12px] mt-[4px]" style={{ color: '#C4C1B5' }}>
              See exactly what a stranger sees if they scan your board.
            </div>
          </div>
          <button
            type="button"
            onClick={onPreviewAsFinder}
            className="font-sans text-[14px] underline"
            style={{ color: 'var(--foam-paper)' }}
          >
            View
          </button>
        </div>
      )}

      {/* Secondary actions */}
      <div className="flex flex-col">
        {[
          { label: 'Transfer ownership', onClick: null /* Phase 3 */ },
          { label: 'Report lost', onClick: null /* Phase 3 */ },
          { label: 'Warranty', onClick: onWarranty },
          { label: 'Support', onClick: onSupport },
        ].map((action, i, arr) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick || undefined}
            className="text-left py-[16px] flex items-center justify-between"
            style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--foam-hair)' : 'none' }}
          >
            <span className="text-[15px]">{action.label}</span>
            <span className="text-foam-ink-2">→</span>
          </button>
        ))}
      </div>

      {/* Auto-save status — Save button retired 2026-06-05 (Nigel).
          Only the error state demands attention; success is quiet. */}
      {saveState === 'error' && (
        <button
          type="button"
          onClick={flush}
          className="
            h-[52px] w-full bg-foam-orange text-foam-carbon
            font-sans font-bold text-[14px] uppercase
            active:scale-[0.99] transition-transform
          "
          style={{ borderRadius: 0, letterSpacing: '0.12em' }}
        >
          ● Save failed — tap to retry
        </button>
      )}

      {isLegacy && (
        <div className="text-[12px] text-foam-ink-2 text-center font-mono tracking-[0.08em] uppercase">
          ● Legacy registration · saving applies new privacy model
        </div>
      )}
    </FPage>
  );
};

export default FoamOwnerDetail;
